import { About, Blog, Gallery, Home, Person, Social, Work } from "@/types";

const person: Person = {
  firstName: "Viskhan",
  lastName: "Khasiyev",
  name: "Viskhan Khasiyev",
  role: "Design Engineer",
  avatar: "/images/upgraded-portrait.jpg",
  email: "khasiyev.viskhan@gmail.com",
  location: "Europe/Brussels",
  languages: ["English", "Dutch", "Russian"],
};

const social: Social = [
  {
    name: "LinkedIn",
    icon: "linkedin",
    link: "https://www.linkedin.com/in/viskhan-khasiyev-45b8822a1",
    essential: true,
  },
  {
    name: "X",
    icon: "twitter",
    link: "https://x.com/hasiyevv",
    essential: true,
  },
  {
    name: "GitHub",
    icon: "github",
    link: "https://github.com/Genghis-dev",
    essential: true,
  },
  {
    name: "Behance",
    icon: "behance",
    link: "https://www.behance.net/viskhankhasiyev",
    essential: true,
  },
  {
    name: "Email",
    icon: "email",
    link: `mailto:${person.email}`,
    essential: true,
  },
];

const home: Home = {
  path: "/",
  image: "/api/og/generate?title=Viskhan%20Khasiyev",
  label: "Home",
  title: "Viskhan Khasiyev",
  description:
    "Design engineer in Antwerp. A professional maker across design, code and automation. I take ideas from first Figma frame to production build, and wire up the workflows that keep them running.",
};

const about: About = {
  path: "/about",
  label: "About",
  title: `About · Viskhan`,
  description: `Design engineer in Antwerp and a professional maker across design, code and automation. Currently at Bundl, with recent client involvements including Nestlé, Lilly, Chanel and Gore.`,
  tableOfContent: {
    display: true,
    subItems: false,
  },
  avatar: {
    display: true,
  },
  calendar: {
    display: true,
    link: "https://cal.com/viskhan-khasiyev-kukmir/30min",
  },
  intro: {
    display: true,
    title: "Bio",
    description: (
      <>
        A professional maker across design, code and automation. I design
        interfaces, build them in code and wire up the workflows around them,
        from first Figma frame to production build. Lately that includes native
        apps and real-time 3D. Based in Antwerp.
        <br />
        <br />
        An AI-augmented workflow with Cursor and Claude Code sits at the core of
        how I ship. Recent client involvements include Nestlé, Lilly, Chanel and
        Gore. I move fast and treat shipping as the only proof that something is
        real.
      </>
    ),
  },
  profile:
    "Design engineer and professional maker across design, code and automation. I design and build fully custom web experiences end to end, from first Figma frame to production build, with an AI-augmented workflow at the core of how I ship. Recent client involvements include Nestlé, Lilly, Chanel and Gore.",
  work: {
    display: true,
    title: "Experience",
    experiences: [
      {
        company: "Bundl",
        timeframe: "Jan 2025 – Present",
        role: "Venture Designer & Developer",
        achievements: [
          <>
            Designer and developer on two fully custom Shopify themes,
            oleus.com for Nestlé and 113spring.com, built as scalable
            component systems their teams launch new pages with, no
            developer needed.
          </>,
          <>
            Built MVPs and prototypes to validate new ventures for enterprise
            clients, including goretexkidswear.com for Gore.
          </>,
          <>
            Translated venture strategy into visual narratives and GTM assets
            for Liberty Global, Nestlé and Gore, aligning large stakeholder
            groups.
          </>,
        ],
        images: [],
      },
      {
        company: "CCM",
        timeframe: "Jun 2020 – Aug 2022",
        role: "Technical Staff",
        achievements: [],
        images: [],
      },
    ],
  },
  studies: {
    display: true,
    title: "Education",
    institutions: [
      {
        name: "Howest Kortrijk",
        detail: "BA Digital Design & Development, Kortrijk",
        time: "2022–2025",
      },
      {
        name: "Hanyang University",
        detail: "Exchange, Communication & Media Studies, Seoul",
        time: "2024",
      },
    ],
  },
  recognition: [
    {
      name: "Awwwards",
      detail: "Honorable Mention, khasiyev.com",
      time: "Aug 2026",
      link: "https://www.awwwards.com/viskhan-khasiyev/",
    },
  ],
  technical: {
    display: true,
    title: "Skills",
    skills: [
      {
        title: "Development",
        description: <></>,
        tags: [
          { name: "TypeScript" },
          { name: "JavaScript" },
          { name: "React" },
          { name: "Next.js" },
          { name: "Three.js" },
          { name: "WebGL/GLSL" },
          { name: "React Native" },
          { name: "Swift" },
          { name: "ElectronJS" },
          { name: "HTML/CSS" },
          { name: "Liquid (Shopify)" },
        ],
        images: [],
      },
      {
        title: "Design",
        description: <></>,
        tags: [
          { name: "Figma" },
          { name: "Framer" },
          { name: "Webflow" },
          { name: "Spline" },
          { name: "Blender" },
          { name: "After Effects" },
          { name: "Adobe CC" },
        ],
        images: [],
      },
      {
        title: "AI & Workflows",
        description: <></>,
        tags: [
          { name: "Cursor" },
          { name: "LangChain" },
          { name: "Claude Code" },
          { name: "Codex" },
          { name: "MCP" },
          { name: "n8n" },
          { name: "Make" },
          { name: "AI image & video generation" },
        ],
        images: [],
      },
    ],
  },
};

const blog: Blog = {
  path: "/blog",
  label: "Blog",
  title: "Writing.",
  description: `Notes on design, development, and the work. Writing about venture building, AI-assisted workflows, and the tools that actually earn their spot in the stack.`,
};

const work: Work = {
  path: "/work",
  label: "Work",
  title: `Work`,
  description: `Selected work by ${person.name}. Fully custom Shopify themes, MVPs, and venture prototypes for Nestlé, Lilly, Chanel and Gore.`,
};

const university: Work = {
  path: "/university",
  label: "University",
  title: `University`,
  description: `Projects from three years at Devine, the digital design and development programme in Kortrijk (2022–2025).`,
};

const lab: Work = {
  path: "/lab",
  label: "Lab",
  title: `Lab`,
  description: `Side projects and experiments by ${person.name}. Built to learn, not always to ship.`,
};

const gallery: Gallery = {
  path: "/gallery",
  label: "Gallery",
  title: `Gallery`,
  description: `A visual collection by ${person.name}.`,
  images: [],
};

export { person, social, home, about, blog, work, university, lab, gallery };
